<script setup lang="ts">
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { computed, nextTick, provide, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStaffStore } from '../stores/staffStore'
import type { IStaffDepartment } from '../types/IStaffDepartment'
import { ORG_CARD_WIDTH, ORG_CHILDREN_GAP, ORG_CARD_MIN_HEIGHT, ORG_CARD_SELECTED_OUTLINE_PX } from '../constants/orgChartLayout'
import StructureSidebar from './StructureSidebar.vue'
import { STAFF_ANCHORS } from '@/modules/onboarding/scenarios/staff/anchors'
import StructureTreeNode from './StructureTreeNode.vue'

interface IConnectorEdge {
  parentId: string
  childId: string
  d: string
  highlighted: boolean
}

interface ICardRect {
  id: string
  cx: number
  top: number
  bottom: number
  left: number
  right: number
}

const CONNECTOR_RADIUS = 15
const TREE_PADDING = 80
const orgCardMinHeightPx = `${ORG_CARD_MIN_HEIGHT}px`

const orgTreeStyle = computed(() => ({
  width: `${treeSize.value.w}px`,
  height: `${treeSize.value.h}px`,
  '--org-card-selection-ring-size': `${ORG_CARD_SELECTED_OUTLINE_PX}px`,
}))

const { smAndDown } = useDisplay()

const viewportEl = ref<HTMLElement | null>(null)
const treeEl = ref<HTMLElement | null>(null)
const zoom = ref(smAndDown.value ? 50 : 100)
const panX = ref(0)
const panY = ref(0)
const treeSize = ref({ w: 800, h: 600 })
const treeOffset = ref({ x: TREE_PADDING, y: TREE_PADDING })
const connectorEdges = ref<IConnectorEdge[]>([])

const zoomInput = ref(String(zoom.value))

/** Zoom к точке (cx, cy) в координатах viewport — для кнопок и pinch */
function setZoomAtPoint(nextZoom: number, cx: number, cy: number) {
  const clamped = Math.max(10, Math.min(500, nextZoom))
  const oldScale = zoom.value / 100
  const newScale = clamped / 100
  if (oldScale === newScale) {
    zoom.value = clamped
    return
  }

  const canvasX = (cx - panX.value) / oldScale
  const canvasY = (cy - panY.value) / oldScale
  zoom.value = clamped
  panX.value = cx - canvasX * newScale
  panY.value = cy - canvasY * newScale
}

function setZoomKeepingCenter(nextZoom: number) {
  const viewport = viewportEl.value
  if (!viewport) {
    zoom.value = Math.max(10, Math.min(500, nextZoom))
    return
  }
  setZoomAtPoint(nextZoom, viewport.clientWidth / 2, viewport.clientHeight / 2)
}

function zoomIn() {
  if (zoom.value < 500)
    setZoomKeepingCenter(Math.min(500, zoom.value + 10))
}

function zoomOut() {
  if (zoom.value > 10)
    setZoomKeepingCenter(Math.max(10, zoom.value - 10))
}

watch(zoom, (val) => {
  zoomInput.value = String(val)
})

function onZoomInputBlur() {
  let val = parseInt(zoomInput.value, 10)
  if (isNaN(val))
    val = zoom.value
  else
    val = Math.max(10, Math.min(500, val))

  setZoomKeepingCenter(val)
  zoomInput.value = String(val)
}

const isDragging = ref(false)
let panStartX = 0
let panStartY = 0
let panOriginX = 0
let panOriginY = 0
let hasMoved = false
let isPinching = false
let pinchStartDist = 1
let pinchStartZoom = 100

function beginPan(clientX: number, clientY: number) {
  isDragging.value = true
  hasMoved = false
  panStartX = clientX
  panStartY = clientY
  panOriginX = panX.value
  panOriginY = panY.value
}

function applyPan(clientX: number, clientY: number) {
  if (!isDragging.value) return
  const dx = clientX - panStartX
  const dy = clientY - panStartY
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3)
    hasMoved = true
  panX.value = panOriginX + dx
  panY.value = panOriginY + dy
}

function startPan(e: MouseEvent) {
  if (e.button !== 0) return
  isPinching = false
  beginPan(e.clientX, e.clientY)
}

function onPan(e: MouseEvent) {
  if (!isDragging.value || isPinching) return
  e.preventDefault()
  applyPan(e.clientX, e.clientY)
}

function stopPan() {
  isDragging.value = false
}

function getTouchPairMetrics(touches: TouchList) {
  const viewport = viewportEl.value
  const a = touches[0]
  const b = touches[1]
  const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1
  if (!viewport) {
    return {
      dist,
      cx: (a.clientX + b.clientX) / 2,
      cy: (a.clientY + b.clientY) / 2,
    }
  }
  const rect = viewport.getBoundingClientRect()
  return {
    dist,
    cx: (a.clientX + b.clientX) / 2 - rect.left,
    cy: (a.clientY + b.clientY) / 2 - rect.top,
  }
}

function onTouchStart(e: TouchEvent) {
  if (e.touches.length === 1) {
    isPinching = false
    beginPan(e.touches[0].clientX, e.touches[0].clientY)
    return
  }

  if (e.touches.length >= 2) {
    isPinching = true
    isDragging.value = false
    const metrics = getTouchPairMetrics(e.touches)
    pinchStartDist = metrics.dist
    pinchStartZoom = zoom.value
    hasMoved = true
  }
}

function onTouchMove(e: TouchEvent) {
  if (isPinching && e.touches.length >= 2) {
    const metrics = getTouchPairMetrics(e.touches)
    setZoomAtPoint(pinchStartZoom * (metrics.dist / pinchStartDist), metrics.cx, metrics.cy)
    return
  }

  if (!isDragging.value || e.touches.length !== 1) return
  applyPan(e.touches[0].clientX, e.touches[0].clientY)
}

function onTouchEnd(e: TouchEvent) {
  if (e.touches.length === 0) {
    isDragging.value = false
    isPinching = false
    return
  }

  /* После pinch остался один палец — продолжаем pan от текущей позиции */
  if (e.touches.length === 1) {
    isPinching = false
    beginPan(e.touches[0].clientX, e.touches[0].clientY)
    hasMoved = true
  }
}

function onViewportClick(e: MouseEvent) {
  if (hasMoved) {
    e.stopPropagation()
    e.preventDefault()
    hasMoved = false
    return
  }
  const target = e.target as HTMLElement
  if (!target.closest('.dept-node-card'))
    isSidebarOpen.value = false
}

const props = defineProps<{
  modelValue: boolean

  /** Раскрыть первое подразделение с вложенными при открытии — онбординг показывает, как выглядит иерархия */
  expandFirstOnOpen?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select-employee': [employeeId: string]
}>()

const staffStore = useStaffStore()
const onDialogDismissAttempt = useDialogDismissGuard(closeModal, 'StructureModal')

const searchQuery = ref('')
provide('searchQuery', searchQuery)

function readHideHierarchyPreference(): boolean {
  const stored = localStorage.getItem('staff_tree_hide_hierarchy')
  if (stored === null)
    return true
  return stored === 'true'
}
const selectedDeptId = ref<string | null>(null)
const selectedDeptName = ref('')
const isSidebarOpen = ref(false)

/** Подсветка карточки и пути root→узел только при открытом сайдбаре (как в Bitrix) */
const highlightedDeptId = computed(() =>
  isSidebarOpen.value ? selectedDeptId.value : null,
)

const expandedIds = ref<string[]>([])
const isHideHierarchyActive = ref(readHideHierarchyPreference())

/** Ширина колонки по раскрытому поддереву — при поиске и «вся иерархия», не в режиме аккордеона */
const orgFullTreeLayout = computed(() => {
  if (searchQuery.value?.trim())
    return true
  return !isHideHierarchyActive.value
})
provide('orgFullTreeLayout', orgFullTreeLayout)

function applyExpandedFromPreference() {
  const depts = (staffStore.departments || []) as IStaffDepartment[]
  const roots = depts.filter(d => d.id !== 'empty')
  expandedIds.value = isHideHierarchyActive.value
    ? []
    : getIdsWithChildren(roots)
}

watch(isHideHierarchyActive, (newVal) => {
  localStorage.setItem('staff_tree_hide_hierarchy', String(newVal))
  applyExpandedFromPreference()
  scheduleLayoutUpdate(true)
})

function closeModal() {
  emit('update:modelValue', false)
}

async function selectDepartment(id: string, name: string) {
  selectedDeptId.value = String(id)
  selectedDeptName.value = name
  isSidebarOpen.value = true
  scheduleLayoutUpdate()
  await staffStore.fetchDepartmentEmployees(id)
}

function collectDescendantIds(node: IStaffDepartment, acc: Set<string>) {
  for (const child of node.children || []) {
    if (!child?.id || child.id === 'empty') continue
    acc.add(String(child.id))
    collectDescendantIds(child, acc)
  }
}

function findNodeContext(
  nodes: IStaffDepartment[],
  targetId: string,
  siblings: IStaffDepartment[] = nodes,
): { node: IStaffDepartment, siblings: IStaffDepartment[] } | null {
  for (const node of nodes) {
    if (!node || node.id === 'empty') continue
    if (String(node.id) === String(targetId))
      return { node, siblings }

    if (node.children?.length) {
      const found = findNodeContext(node.children, targetId, node.children)
      if (found) return found
    }
  }
  return null
}

function toggleDeptExpand(deptId: string) {
  if (!isHideHierarchyActive.value)
    return

  const depts = filteredDepartments.value
  const ctx = findNodeContext(depts, deptId)
  if (!ctx) return

  const id = String(deptId)
  const next = new Set(expandedIds.value)
  const isOpen = next.has(id)

  if (isOpen) {
    next.delete(id)
    const descendants = new Set<string>()
    collectDescendantIds(ctx.node, descendants)
    descendants.forEach(d => next.delete(d))
  }
  else {
    for (const sib of ctx.siblings) {
      const sibId = String(sib.id)
      if (sibId === id) continue
      next.delete(sibId)
      const descendants = new Set<string>()
      collectDescendantIds(sib, descendants)
      descendants.forEach(d => next.delete(d))
    }
    next.add(id)
  }

  expandedIds.value = [...next]
  scheduleLayoutUpdate()
}

function getIdsWithChildren(nodes: IStaffDepartment[]): string[] {
  const ids: string[] = []
  function walk(items: IStaffDepartment[]) {
    for (const item of items) {
      if (!item || item.id === 'empty') continue
      if (item.children?.length) {
        ids.push(String(item.id))
        walk(item.children)
      }
    }
  }
  walk(nodes)
  return ids
}

const filteredDepartments = computed<IStaffDepartment[]>(() =>
  ((staffStore.departments || []) as IStaffDepartment[]).filter(d => d.id !== 'empty'),
)

const isAnimationFinished = ref(false)

function onDialogAfterEnter() {
  isAnimationFinished.value = true
  scheduleLayoutUpdate(true)
}

function buildManhattanPath(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  opts?: { startInset?: number, endInset?: number, busY?: number },
): string {
  const r = CONNECTOR_RADIUS
  const startInset = opts?.startInset ?? 0
  const endInset = opts?.endInset ?? 0
  const yStart = y1 + startInset
  const midY = opts?.busY ?? (y1 + y2) / 2
  const endY = y2 - endInset

  if (Math.abs(x1 - x2) < 1)
    return `M${x1} ${yStart}V${endY}`

  const goingRight = x2 > x1
  const sweep1 = goingRight ? 0 : 1
  const sweep2 = goingRight ? 1 : 0
  const dx = goingRight ? r : -r

  return [
    `M${x1} ${yStart}`,
    `V${midY - r}`,
    `a${r},${r} 0 0 ${sweep1} ${dx},${r}`,
    `H${x2 - dx}`,
    `a${r},${r} 0 0 ${sweep2} ${dx},${r}`,
    `V${endY}`,
  ].join('')
}

function findPathIds(nodes: IStaffDepartment[], targetId: string, trail: string[] = []): string[] | null {
  for (const node of nodes) {
    if (!node || node.id === 'empty') continue
    const next = [...trail, String(node.id)]
    if (String(node.id) === String(targetId))
      return next
    if (node.children?.length) {
      const found = findPathIds(node.children, targetId, next)
      if (found) return found
    }
  }
  return null
}

function collectVisibleEdges(
  nodes: IStaffDepartment[],
  parentId: string | null,
  edges: Array<{ parentId: string, childId: string }>,
) {
  for (const node of nodes) {
    if (!node || node.id === 'empty') continue
    const id = String(node.id)
    if (parentId)
      edges.push({ parentId, childId: id })

    if (expandedIds.value.includes(id) && node.children?.length)
      collectVisibleEdges(node.children, id, edges)
  }
}

function measureCards(): ICardRect[] {
  const tree = treeEl.value
  if (!tree) return []

  const treeRect = tree.getBoundingClientRect()
  const scale = zoom.value / 100
  const cards = tree.querySelectorAll<HTMLElement>('.dept-node-card')
  const result: ICardRect[] = []

  cards.forEach((card) => {
    const id = card.id.replace(/^dept-card-/, '')
    if (!id) return
    const r = card.getBoundingClientRect()
    const left = (r.left - treeRect.left) / scale
    const top = (r.top - treeRect.top) / scale
    const width = r.width / scale
    const height = r.height / scale
    result.push({
      id,
      left,
      top,
      right: left + width,
      bottom: top + height,
      cx: left + width / 2,
    })
  })

  return result
}

function updateTreeBounds(cards: ICardRect[]) {
  if (!cards.length) {
    const roots = filteredDepartments.value.length || 1
    treeSize.value = {
      w: roots * ORG_CARD_WIDTH + Math.max(0, roots - 1) * ORG_CHILDREN_GAP + TREE_PADDING * 2,
      h: 400,
    }
    treeOffset.value = { x: TREE_PADDING, y: TREE_PADDING }
    return
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const c of cards) {
    minX = Math.min(minX, c.left)
    minY = Math.min(minY, c.top)
    maxX = Math.max(maxX, c.right)
    maxY = Math.max(maxY, c.bottom)
  }

  treeOffset.value = {
    x: treeOffset.value.x + (TREE_PADDING - minX),
    y: treeOffset.value.y + (TREE_PADDING - minY),
  }

  treeSize.value = {
    w: Math.max(400, Math.ceil(maxX - minX + TREE_PADDING * 2)),
    h: Math.max(300, Math.ceil(maxY - minY + TREE_PADDING * 2)),
  }
}

function rebuildConnectors(cards: ICardRect[]) {
  const byId = new Map(cards.map(c => [c.id, c]))
  const rawEdges: Array<{ parentId: string, childId: string }> = []
  collectVisibleEdges(filteredDepartments.value, null, rawEdges)

  const selectedPath = highlightedDeptId.value
    ? findPathIds(filteredDepartments.value, highlightedDeptId.value)
    : null
  const pathSet = new Set(selectedPath || [])

  const busYByParent = new Map<string, number>()
  const topsByParent = new Map<string, number[]>()
  for (const edge of rawEdges) {
    const parent = byId.get(edge.parentId)
    const child = byId.get(edge.childId)
    if (!parent || !child) continue
    const list = topsByParent.get(edge.parentId) ?? []
    list.push(child.top)
    topsByParent.set(edge.parentId, list)
  }
  for (const [parentId, tops] of topsByParent) {
    const parent = byId.get(parentId)
    if (!parent || !tops.length) continue
    const childTop = Math.min(...tops)
    busYByParent.set(parentId, (parent.bottom + childTop) / 2)
  }

  const edges: IConnectorEdge[] = []
  for (const edge of rawEdges) {
    const parent = byId.get(edge.parentId)
    const child = byId.get(edge.childId)
    if (!parent || !child) continue

    const highlighted = !!(
      selectedPath
      && pathSet.has(edge.parentId)
      && pathSet.has(edge.childId)
      && selectedPath.indexOf(edge.childId) === selectedPath.indexOf(edge.parentId) + 1
    )

    const activeId = highlightedDeptId.value ? String(highlightedDeptId.value) : null
    const startInset = highlighted && activeId === edge.parentId ? ORG_CARD_SELECTED_OUTLINE_PX : 0
    const endInset = highlighted && activeId === edge.childId ? ORG_CARD_SELECTED_OUTLINE_PX : 0

    edges.push({
      parentId: edge.parentId,
      childId: edge.childId,
      highlighted,
      d: buildManhattanPath(
        parent.cx,
        parent.bottom,
        child.cx,
        child.top,
        {
          startInset,
          endInset,
          busY: busYByParent.get(edge.parentId),
        },
      ),
    })
  }

  connectorEdges.value = edges
}

function centerContentInViewport(cards: ICardRect[]) {
  const viewport = viewportEl.value
  if (!viewport) return
  const scale = zoom.value / 100
  const ox = treeOffset.value.x
  const oy = treeOffset.value.y

  if (!cards.length) {
    panX.value = (viewport.clientWidth - treeSize.value.w * scale) / 2
    panY.value = Math.max(24, (viewport.clientHeight - treeSize.value.h * scale) / 8)
    return
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const c of cards) {
    minX = Math.min(minX, c.left)
    minY = Math.min(minY, c.top)
    maxX = Math.max(maxX, c.right)
    maxY = Math.max(maxY, c.bottom)
  }

  const contentCx = ox + (minX + maxX) / 2
  const contentCy = oy + (minY + maxY) / 2
  panX.value = viewport.clientWidth / 2 - contentCx * scale
  panY.value = viewport.clientHeight / 2 - contentCy * scale
}

let layoutRaf = 0
let pendingViewportCenter = false

function scheduleLayoutUpdate(center = false) {
  if (center)
    pendingViewportCenter = true

  cancelAnimationFrame(layoutRaf)
  layoutRaf = requestAnimationFrame(async () => {
    await nextTick()
    /* Два прохода: сдвиг корней так, чтобы absolute-дети не уходили в минус, затем коннекторы */
    updateTreeBounds(measureCards())
    await nextTick()
    updateTreeBounds(measureCards())
    await nextTick()
    const cards = measureCards()
    rebuildConnectors(cards)
    const shouldCenterViewport = pendingViewportCenter || Boolean(searchQuery.value?.trim())
    if (shouldCenterViewport && isAnimationFinished.value) {
      centerContentInViewport(cards)
      pendingViewportCenter = false
    }
  })
}

watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    selectedDeptId.value = null
    selectedDeptName.value = ''
    searchQuery.value = ''
    zoom.value = smAndDown.value ? 50 : 100
    panX.value = 0
    panY.value = 0
    treeOffset.value = { x: TREE_PADDING, y: TREE_PADDING }
    treeSize.value = { w: 800, h: 600 }
    connectorEdges.value = []

    await staffStore.fetchDepartments()
    applyExpandedFromPreference()
    if (props.expandFirstOnOpen && isHideHierarchyActive.value) {
      const firstWithChildren = filteredDepartments.value.find(d => d.id !== 'empty' && d.children?.some(c => c.id !== 'empty'))
      if (firstWithChildren)
        expandedIds.value = [String(firstWithChildren.id)]
    }
    isSidebarOpen.value = false
    scheduleLayoutUpdate(true)
  }
  else {
    isAnimationFinished.value = false
  }
})

watchDebounced(searchQuery, async newQuery => {
  const query = newQuery?.trim()
  await staffStore.fetchDepartments(query)

  if (query)
    expandedIds.value = getIdsWithChildren(filteredDepartments.value)
  else
    applyExpandedFromPreference()

  scheduleLayoutUpdate(true)
}, { debounce: 300 })

watch(expandedIds, () => scheduleLayoutUpdate(), { deep: true })
watch(filteredDepartments, () => scheduleLayoutUpdate(), { deep: true })
watch(selectedDeptId, () => scheduleLayoutUpdate())
watch(isSidebarOpen, () => scheduleLayoutUpdate())

function findDeptById(depts: IStaffDepartment[], id: string): IStaffDepartment | null {
  for (const d of depts) {
    if (String(d.id) === String(id)) return d
    if (d.children?.length) {
      const found = findDeptById(d.children, id)
      if (found) return found
    }
  }
  return null
}

const selectedDeptManager = computed(() => {
  if (!selectedDeptId.value || selectedDeptId.value === 'mo') return null
  const depts = (staffStore.departments || []) as IStaffDepartment[]
  const dept = findDeptById(depts, selectedDeptId.value)
  return dept?.manager || null
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    scrollable
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
    @after-enter="onDialogAfterEnter"
    content-class="structure-modal-dialog"
    :scrim="smAndDown"
    fullscreen
  >
    <VCard class="structure-modal-card pa-0 d-flex flex-column position-relative">
      <div
        class="d-flex border-b gap-4 transition-all"
        :class="smAndDown ? 'flex-column align-stretch px-4 py-3' : 'align-center px-6 py-4'"
      >
        <div class="d-flex align-center justify-space-between flex-grow-1">
          <span class="text-h4 font-weight-bold">Структура компании</span>
          <VBtn
            v-if="smAndDown"
            icon="ri-close-line"
            variant="text"
            density="comfortable"
            @click="closeModal"
          />
        </div>
        <VTextField
          v-model="searchQuery"
          placeholder="Поиск"
          prepend-inner-icon="ri-search-line"
          variant="underlined"
          density="compact"
          class="search-underlined"
          :class="smAndDown ? 'w-100' : 'ms-auto'"
          :style="smAndDown ? 'max-width: 100%;' : 'max-width: 380px; width: 100%;'"
          clearable
          persistent-clear
          hide-details
        />
        <VCheckbox
          v-model="isHideHierarchyActive"
          label="Скрывать иерархию"
          hide-details
          density="compact"
          class="compact-checkbox"
          :class="smAndDown ? 'w-100' : 'ms-4'"
        />
        <VBtn
          v-if="!smAndDown"
          icon="ri-close-line"
          variant="text"
          density="comfortable"
          class="ms-4"
          @click="closeModal"
        />
      </div>

      <VCardText class="pa-0 flex-grow-1 overflow-hidden" :data-onboarding="STAFF_ANCHORS.structure.tree">
        <div class="d-flex h-100 align-stretch position-relative">
          <div class="pa-6 d-flex flex-column h-100 overflow-hidden flex-grow-1 position-relative">
            <div
              ref="viewportEl"
              class="org-viewport flex-grow-1 cursor-grab"
              :class="{ 'cursor-grabbing': isDragging }"
              @mousedown="startPan"
              @mousemove="onPan"
              @mouseup="stopPan"
              @mouseleave="stopPan"
              @touchstart.passive="onTouchStart"
              @touchmove.prevent="onTouchMove"
              @touchend="onTouchEnd"
              @touchcancel="onTouchEnd"
              @click.capture="onViewportClick"
            >
              <div
                class="org-canvas"
                :style="{
                  transform: `translate(${panX}px, ${panY}px) scale(${zoom / 100})`,
                }"
              >
                <div
                  ref="treeEl"
                  class="org-tree"
                  :style="orgTreeStyle"
                >
                  <svg
                    class="org-connectors"
                    :width="treeSize.w"
                    :height="treeSize.h"
                  >
                    <defs>
                      <marker
                        id="org-connector-arrow"
                        markerUnits="userSpaceOnUse"
                        markerWidth="20"
                        markerHeight="12"
                        refX="10"
                        refY="10.5"
                      >
                        <path
                          d="M1 1L10 10L19 1"
                          class="org-connectors__arrow"
                        />
                      </marker>
                    </defs>
                    <path
                      v-for="(edge, idx) in connectorEdges"
                      :key="`${edge.parentId}-${edge.childId}-${idx}`"
                      :d="edge.d"
                      class="org-connectors__path"
                      :class="{ '--highlighted': edge.highlighted }"
                      :marker-end="edge.highlighted ? 'url(#org-connector-arrow)' : undefined"
                    />
                  </svg>

                  <div
                    v-if="filteredDepartments.length"
                    class="org-tree__roots"
                    :style="{
                      left: `${treeOffset.x}px`,
                      top: `${treeOffset.y}px`,
                    }"
                  >
                    <StructureTreeNode
                      v-for="dept in filteredDepartments"
                      :key="dept.id"
                      :node="dept"
                      :selected-dept-id="highlightedDeptId"
                      :expanded-ids="expandedIds"
                      @select="selectDepartment"
                      @toggle-expand="toggleDeptExpand"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="zoom-control-panel d-flex align-center bg-surface border rounded px-1 py-0.5 shadow-sm">
              <VBtn
                icon="ri-add-line"
                variant="text"
                density="comfortable"
                :disabled="zoom >= 500"
                @click="zoomIn"
              />
              <div class="d-flex align-center justify-center mx-1" style="min-width: 50px;">
                <input
                  v-model="zoomInput"
                  type="text"
                  class="zoom-input text-body-2 font-weight-bold text-center"
                  style="width: 32px; outline: none; border: none; background: transparent; text-align: center; color: inherit;"
                  @blur="onZoomInputBlur"
                  @keydown.enter="onZoomInputBlur"
                />
                <span class="text-body-2 font-weight-bold select-none" style="margin-left: -2px;">%</span>
              </div>
              <VBtn
                icon="ri-subtract-line"
                variant="text"
                density="comfortable"
                :disabled="zoom <= 10"
                @click="zoomOut"
              />
            </div>
          </div>
        </div>
      </VCardText>

      <VDialog
        :model-value="isSidebarOpen"
        @click:outside="isSidebarOpen = false"
        @keydown.esc.stop="isSidebarOpen = false"
        :scrim="false"
        persistent
        attach=".structure-modal-card"
        class="structure-sidebar-dialog"
        transition="slide-x-reverse-transition"
      >
        <div class="d-flex flex-column h-100 w-100" v-if="isSidebarOpen">
          <StructureSidebar
            :department-id="selectedDeptId === 'mo' ? null : selectedDeptId"
            :department-name="selectedDeptName"
            :employees="staffStore.currentDepartmentEmployees"
            :manager-info="selectedDeptManager"
            @close="isSidebarOpen = false"
            @select-employee="emit('select-employee', $event)"
          />
        </div>
      </VDialog>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.structure-modal-card {
  border-radius: 12px;
  height: 100% !important;
  max-height: 100% !important;
}

.transition-all {
  transition: all 0.25s ease-in-out;
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }
  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}

:deep(.structure-sidebar-dialog) {
  .v-overlay__content {
    position: absolute !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    margin: 0 !important;
    width: 375px !important;
    max-width: 90vw !important;
    background-color: rgb(var(--v-theme-surface)) !important;
    box-shadow: -6px 0 18px -4px rgba(0, 0, 0, 0.15) !important;
    z-index: 110 !important;
    border-radius: 0 12px 12px 0 !important;
    height: 100% !important;
    max-height: 100% !important;

    @media (max-width: 600px) {
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      z-index: 100 !important;
      border-radius: 0 !important;
    }
  }

  .v-overlay__content.slide-x-reverse-transition-enter-from,
  .v-overlay__content.slide-x-reverse-transition-leave-to {
    opacity: 1 !important;
    transform: translateX(100%) !important;
  }

  .v-overlay__content.slide-x-reverse-transition-enter-active,
  .v-overlay__content.slide-x-reverse-transition-leave-active {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
    opacity: 1 !important;
  }
}

.org-viewport {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  touch-action: none;

  &.cursor-grab {
    cursor: grab;
    user-select: none;
  }

  &.cursor-grabbing {
    cursor: grabbing;
    user-select: none;
  }
}

.org-canvas {
  transform-origin: 0 0;
  will-change: transform;
}

.org-tree {
  position: relative;
  overflow: visible;
}

.org-tree__roots {
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 24px;
  width: max-content;
  z-index: 1;
}

.org-connectors {
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  pointer-events: none;
  z-index: 0;
}

.org-connectors__path {
  fill: none;
  stroke: rgb(var(--v-theme-secondary));
  stroke-width: 2px;
}

.org-connectors__path.--highlighted {
  stroke: rgb(var(--v-theme-primary));
}

.org-connectors__arrow {
  fill: none;
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 2px;
}

/* Карточка — визуал без изменений; min-height + stretch в ряду как в Bitrix */
.org-tree :deep(.dept-node-card) {
  width: 380px;
  min-height: v-bind(orgCardMinHeightPx);
  height: 100%;
  box-sizing: border-box;
  background: rgb(var(--v-theme-surface));
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.org-tree :deep(.dept-node-card:hover) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(var(--v-theme-primary), 0.15);
  border-color: rgb(var(--v-theme-primary)) !important;
}

.org-tree :deep(.dept-header) {
  min-height: 52px;
}

.org-tree :deep(.dept-details) {
  min-height: 72px;
}

.org-tree :deep(.bottom-action-btn),
.org-tree :deep(.bottom-action-btn.disabled) {
  min-height: 40px;
  box-sizing: border-box;
}

.org-tree :deep(.dept-node-card.selected) {
  border-color: rgb(var(--v-theme-primary-100)) !important;
  box-shadow:
    0 0 0 var(--org-card-selection-ring-size) rgb(var(--v-theme-primary-100)),
    0 4px 12px rgba(46, 38, 61, 0.2) !important;
}

.org-tree :deep(.dept-node-card.mo-card) {
  border-color: rgb(var(--v-theme-primary)) !important;
}

.org-tree :deep(.custom-chip) {
  font-size: 11px;
  line-height: 1;
  color: rgba(var(--v-theme-on-surface), 0.7);
  border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}

.org-tree :deep(.bg-light) {
  background-color: rgba(var(--v-theme-on-surface), 0.015);
}

.org-tree :deep(.bottom-action-btn) {
  margin: 0 16px 16px 16px;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
  color: rgb(var(--v-theme-primary)) !important;
  transition: all 0.2s ease;
  cursor: pointer;
  margin-top: auto;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.org-tree :deep(.bottom-action-btn:hover:not(.disabled)) {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

.org-tree :deep(.bottom-action-btn.disabled) {
  margin: 0 16px 16px 16px;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  color: rgba(var(--v-theme-on-surface), 0.4);
  cursor: default;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.org-tree :deep(.accent-color) {
  color: rgb(var(--v-theme-primary)) !important;
}

.org-tree :deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}

.org-tree :deep(.v-theme--dark .search-highlight) {
  background-color: rgba(var(--v-theme-primary-500), 0.38) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}

.zoom-control-panel {
  position: absolute;
  left: 24px;
  bottom: 48px;
  z-index: 10;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  background-color: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;

  :deep(.v-btn) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    padding: 0;
    color: rgba(var(--v-theme-on-surface), 0.6) !important;

    &:hover {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}
</style>
